import React, { useState, createRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import classNames from 'classnames';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Tooltip from '@material-ui/core/Tooltip';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import ChatStore from 'stores/ChatStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import {
  ACCESS_LEVEL_STR,
  isAnyTypeOfGold,
} from 'pages/User/AccessLevel';
import ImageIcon from 'components/ChatMessageTypes/components/ImageIcon';
import {
  MESSAGE_FLAIRS,
  MESSAGE_TYPES,
} from 'enum/messages';
import { focusChatInputField } from 'utils/helpers';

import fanclubIcon from '../../../../../../../../images/fanclub-icon.png';
import goldStar from '../../../../../../../../images/user/gold-star.png';
import ContextualMenu from '../../UserListTab/ContextualMenu/ContextualMenu';
import RecentSpendingTierIndicator from '../RecentSpendingTierIndicator';

import Conversation from './Conversation/Conversation';
import styles from './Conversations.css';


function Conversations(props) {
  const {
    activeConversationKey,
    activeReadConversations,
    isBroadcasterChat,
    list,
    onchangeTab,
    options,
    user,
  } = props;

  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const [tabValue, setTabValue] = useState('');

  const {
    activeConversation,
    activeConversationNames,
    deleteChat,
    hiddenChats,
    isTherePrivateChatWithAdmin,
    privateUserChats,
    showChat,
  } = ChatStore;
  const { isUserSilenced } = ProfileStore;
  const { performerTopTipper } = TippingStore;

  let orderChatTabs = activeConversationNames;
  if (isTherePrivateChatWithAdmin) {
    const filteredConversations = activeConversationNames.filter(
      (item) => item !== isTherePrivateChatWithAdmin
    );

    orderChatTabs = [isTherePrivateChatWithAdmin, ...filteredConversations];
  }

  const userInfo = options.find((userEl) => userEl?.value?.toLowerCase() === tabValue?.toLowerCase());
  const isSilenced = userInfo?.isSilenced;
  const isRoomModerator = userInfo?.isRoomModerator;

  useEffect(() => {
    if (!activeConversation && orderChatTabs.length) {
      ChatStore.setActiveConversation(orderChatTabs[0]);
    }
  }, [orderChatTabs, activeConversation]);

  useEffect(() => {
    ChatStore.setActiveConversation(activeConversationKey);
  }, [activeConversationKey]);

  const handleChange = useCallback((event, newValue) => {
    onchangeTab(newValue);
    focusChatInputField();
  }, [onchangeTab]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = useCallback((userKey, ref) => {
    setTabValue(userKey);
    setAnchorEl(ref.current);
    setShowContextualMenu(!showContextualMenu);
  }, [showContextualMenu]);

  const onHandleOpenMenu = useCallback((userKey, ref) => () => {
    handleOpenMenu(userKey, ref);
  }, [handleOpenMenu]);

  const handleModalConfirm = useCallback(() => {
    deleteChat(tabValue);
    const newList = activeConversationNames.filter((value) => !hiddenChats.includes(value));
    onchangeTab(newList[0]);
  }, [activeConversationNames, hiddenChats, tabValue, deleteChat, onchangeTab]);

  const closeContextualMenu = useCallback(() => {
    setShowContextualMenu(false);
  }, []);

  const deleteConversation = useCallback(() => {
    closeContextualMenu();
    ModalService.showModal(ModalService.MODAL_KEYS.CLOSE_CONVERSATION_MODAL, { continueFn: handleModalConfirm });
  }, [handleModalConfirm, closeContextualMenu]);

  // eslint-disable-next-line complexity
  const chatTabRender = orderChatTabs.map((key) => {
    const lastMsgNotFromLoggedInUser =
      list[key] &&
      list[key].messages.length > 0 &&
      list[key].messages[list[key].messages.length - 1].senderOriginalUsername !== user.username;
    const currMsg = list[key];
    const lastMsg = activeReadConversations[key];

    let hasUnreadMsg = false;
    if (
      (!lastMsg || currMsg?.lastMessageTimestamp > lastMsg?.lastReadTimestamp) &&
      lastMsgNotFromLoggedInUser
    ) {
      hasUnreadMsg = true;
    }
    const isClosed = hiddenChats.includes(key) && !hasUnreadMsg;
    if (hiddenChats.includes(key) && !isClosed) {
      showChat(key);
    }
    const ref = createRef();
    const info = options.find((userEl) => userEl?.value?.toLowerCase() === key?.toLowerCase());

    const isAdminMessageTab = [MESSAGE_TYPES.ADMIN, MESSAGE_TYPES.MODERATOR, MESSAGE_TYPES.USER_MODERATOR].includes(
      privateUserChats[key].accessLevel
    );

    let label = isAdminMessageTab ? MESSAGE_FLAIRS.ADMIN : key;
    if (isBroadcasterChat) {
      const isGold = info && isAnyTypeOfGold(info.accessLevel);
      const isInFanclub = info?.isFanclubSubscribed === '1';

      const isCoach = info?.accessLevel === ACCESS_LEVEL_STR.COACH;

      const roomModeratorIcon = !isCoach && info?.isRoomModerator === '1';
      const isTipLeader = !isCoach && !roomModeratorIcon &&
        performerTopTipper?.toLowerCase() === info?.value?.toLowerCase();
      label = (
        <div className={styles.label}>
          {!isCoach && (
            <RecentSpendingTierIndicator
              hasTokens={Boolean(info?.hasTokens)}
              recentSpendingTier={info?.recentSpendingTier}
            />
          )}
          {isGold &&
            <Tooltip arrow={true} placement="bottom" title={translate('v2broadcast.gold.user')}>
              <img alt="gold" className={styles.icon} src={goldStar} />
            </Tooltip>
          }
          {!isGold && isInFanclub && (
            <Tooltip arrow={true} placement="bottom" title={translate('profile.profile.fanclub.member')}>
              <img alt="fanclub" className={styles.icon} src={fanclubIcon} />
            </Tooltip>
          )}
          <span>{label}</span>
          {isCoach && <span className={styles.coach}>{translate('v2profile.chat.coach')}</span>}
          {roomModeratorIcon && <span className={styles.mod}>{translate('v2profile.chat.mod')}</span>}
          {isTipLeader &&
            <Tooltip arrow={true} placement="bottom" title={translate('v2profile.tip.leader.title')}>
              <span>
                <ImageIcon type="crown" />
              </span>
            </Tooltip>
          }
        </div>
      );
    }

    const tabClasses = classNames({
      [styles.conversationTab]: true,
      [styles.hasUnreadMsg]: hasUnreadMsg,
      [styles.closed]: isClosed,
    });

    return (
      <Tab
        key={`chatTab-${key}`}
        className={tabClasses}
        classes={{
          root: styles.tabRoot,
          wrapper: isAdminMessageTab ? styles.tabLabelForAdmins : styles.tabLabel,
          selected: styles.selected,
        }}
        icon={
          isAdminMessageTab ? null : (
            <MoreVertIcon ref={ref} className={styles.moreButton} onClick={onHandleOpenMenu(key, ref)} />
          )
        }
        label={label}
        value={key}
      />
    );
  });

  return (
    <div className={styles.conversationsContainer}>
      <Tabs
        className={styles.tabs}
        classes={{
          root: styles.tabsRoot,
          indicator: styles.indicator,
        }}
        orientation="vertical"
        value={activeConversationKey}
        variant="scrollable"
        onChange={handleChange}
      >
        {chatTabRender}
      </Tabs>
      {activeConversationKey && <Conversation index={0} user={user} value={activeConversationKey} {...props} />}
      <ContextualMenu
        anchorEl={anchorEl}
        canDeleteConversation={true}
        close={closeContextualMenu}
        confirmConversationDelete={handleModalConfirm}
        deleteConversation={deleteConversation}
        isBroadcasterChat={isBroadcasterChat}
        isConversation={true}
        isDesktop={true}
        isRoomModerator={isRoomModerator}
        isSilenced={isSilenced}
        isUserSilenced={isUserSilenced}
        originalUsername={tabValue}
        showContextualMenu={showContextualMenu}
      />
    </div>
  );
}

Conversations.propTypes = {
  list: PropTypes.object.isRequired,
  onchangeTab: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  activeConversationKey: PropTypes.string,
  activeReadConversations: PropTypes.object,
  isBroadcasterChat: PropTypes.bool,
};

Conversations.defaultProps = {
  activeConversationKey: null,
  activeReadConversations: {},
  isBroadcasterChat: false,
};

export default observer(Conversations);
